import {
  Alert,
  Award,
  Bit,
  Calendar,
  Christmas,
  Emoji,
  Info,
  Live,
  Mail,
  Message,
  News,
  Rocket,
  Star,
  Timer,
  Unlock,
} from '@onesource/icons';
import { PromoBannerIconScheme } from '@onesource/schemas';
import type { ComponentType, SVGProps } from 'react';

export type PromoBannerIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface PromoBannerIconSchemeConfig {
  /** Human-readable label for the selector (dashboard form). */
  label: string;
  /** SVG icon component (uses `currentColor`, so the consumer sets the color). */
  Icon: PromoBannerIconComponent;
}

/**
 * Single source of truth for promo-banner badge icons. Kept separate from the
 * lesson icon scheme so promo branding can change independently. Order here is
 * the order shown in the selector.
 */
export const PROMO_BANNER_ICON_SCHEMES: Record<PromoBannerIconScheme, PromoBannerIconSchemeConfig> = {
  [PromoBannerIconScheme.ROCKET]: { label: 'Rocket', Icon: Rocket },
  [PromoBannerIconScheme.STAR]: { label: 'Star', Icon: Star },
  [PromoBannerIconScheme.AWARD]: { label: 'Award', Icon: Award },
  [PromoBannerIconScheme.ALERT]: { label: 'Alert', Icon: Alert },
  [PromoBannerIconScheme.BIT]: { label: 'Bit', Icon: Bit },
  [PromoBannerIconScheme.CALENDAR]: { label: 'Calendar', Icon: Calendar },
  [PromoBannerIconScheme.CHRISTMAS]: { label: 'Christmas', Icon: Christmas },
  [PromoBannerIconScheme.EMOJI]: { label: 'Emoji', Icon: Emoji },
  [PromoBannerIconScheme.INFO]: { label: 'Info', Icon: Info },
  [PromoBannerIconScheme.LIVE]: { label: 'Live', Icon: Live },
  [PromoBannerIconScheme.MAIL]: { label: 'Mail', Icon: Mail },
  [PromoBannerIconScheme.MESSAGE]: { label: 'Message', Icon: Message },
  [PromoBannerIconScheme.NEWS]: { label: 'News', Icon: News },
  [PromoBannerIconScheme.TIMER]: { label: 'Timer', Icon: Timer },
  [PromoBannerIconScheme.UNLOCK]: { label: 'Unlock', Icon: Unlock },
};

/** Ordered icon-scheme options for selectors, derived from the source map. */
export const PROMO_BANNER_ICON_SCHEME_OPTIONS = (Object.keys(PROMO_BANNER_ICON_SCHEMES) as PromoBannerIconScheme[]).map(
  value => ({
    value,
    ...PROMO_BANNER_ICON_SCHEMES[value],
  }),
);

/**
 * Resolve a promo-banner icon scheme to its icon component, falling back to
 * ROCKET when the value is missing or not a known scheme.
 */
export const getPromoBannerIconScheme = (
  iconScheme?: PromoBannerIconScheme | string | null,
): PromoBannerIconComponent =>
  (
    PROMO_BANNER_ICON_SCHEMES[iconScheme as PromoBannerIconScheme] ??
    PROMO_BANNER_ICON_SCHEMES[PromoBannerIconScheme.ROCKET]
  ).Icon;
